import { useState, useRef } from "react";
import { Upload, X, Trash2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface HeroVideo {
  id: string;
  title: string;
  video_url: string;
}

interface HeroVideoUploaderProps {
  heroVideo: HeroVideo | null;
}

export function HeroVideoUploader({ heroVideo }: HeroVideoUploaderProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setIsUploading(true);

      const fileExt = file.name.split(".").pop();
      const fileName = `hero-${Date.now()}.${fileExt}`;
      const filePath = `hero/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("videos")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("videos")
        .getPublicUrl(filePath);

      if (heroVideo) {
        await supabase.from("welcome_videos").delete().eq("id", heroVideo.id);
      }

      const { error: dbError } = await supabase.from("welcome_videos").insert({
        title: "Hero Video",
        video_url: urlData.publicUrl,
        display_order: 0,
        video_type: "hero",
      });

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["welcome-videos"] });
      queryClient.invalidateQueries({ queryKey: ["hero-video"] });
      setSelectedFile(null);
      toast({ title: "Hero video uploaded!" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to upload video",
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsUploading(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!heroVideo) return;

      const { error } = await supabase
        .from("welcome_videos")
        .delete()
        .eq("id", heroVideo.id);

      if (error) throw error;

      try {
        const url = new URL(heroVideo.video_url);
        const pathParts = url.pathname.split("/storage/v1/object/public/videos/");
        if (pathParts[1]) {
          await supabase.storage.from("videos").remove([pathParts[1]]);
        }
      } catch {
        // Ignore storage deletion errors
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["welcome-videos"] });
      queryClient.invalidateQueries({ queryKey: ["hero-video"] });
      toast({ title: "Hero video deleted" });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete video",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("video/")) {
        toast({
          title: "Invalid file type",
          description: "Please select a video file",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  return (
    <div className="mb-8 p-4 bg-card border border-border rounded-lg">
      <h3 className="text-lg font-medium text-foreground mb-4">Hero Video</h3>
      
      {heroVideo ? (
        <div className="space-y-4">
          <div className="aspect-video bg-muted rounded-lg overflow-hidden">
            <video
              src={heroVideo.video_url}
              controls
              className="w-full h-full object-cover"
              preload="metadata"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              Replace
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
          >
            {selectedFile ? (
              <div className="flex items-center justify-center gap-2">
                <span className="text-foreground">{selectedFile.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-muted-foreground">
                <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Click to upload hero video</p>
                <p className="text-sm mt-1">This appears under the OZ PERFORMANCE header</p>
              </div>
            )}
          </div>
          {selectedFile && (
            <Button
              onClick={() => uploadMutation.mutate(selectedFile)}
              disabled={isUploading}
              className="w-full"
            >
              {isUploading ? "Uploading..." : "Upload Hero Video"}
            </Button>
          )}
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
